import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { PlayCircleOutline } from '@eqi-investimentos/icons/web';
import { Flex, Text } from '@eqi-investimentos/react';
import { PATHS } from '@/config/paths';
import { TopContent } from '@/modules/Entertainment/http/client/useGetTopContent';
import { getPTBRDateAndRemoveYear } from '@/utils/date';

export const TopContentCard: FC<TopContentCardProps> = ({
  path,
  position,
  programTitle,
  publishedAt,
  thumb,
  title,
}) => {
  return (
    <Flex gap="2xl">
      <Text fontSize="7.5rem" fontWeight="bold" lineHeight="13.75rem">
        {position}
      </Text>
      <Flex
        as={Link}
        href={`${PATHS.hubs.content}/${path}`}
        position="relative"
        flexDir="column"
        w="18rem"
        h="19.5rem"
        bg={`linear-gradient(180deg, rgba(3, 2, 10, 0.20) 21%, #000 100%)`}
        borderRadius="0.3125rem"
        cursor="pointer"
      >
        <Flex
          position="absolute"
          left="0"
          w="18rem"
          h="19.5rem"
          borderRadius="md"
          overflow="hidden"
        >
          <Flex
            position="absolute"
            left="0"
            w="full"
            h="full"
            flexDir="column"
            justifyContent="flex-end"
            gap="xs"
            p="xl"
            zIndex="20"
          >
            <Flex pos="relative" w="2.75rem" h="3.4375rem">
              <PlayCircleOutline
                mb="2.5"
                w="2.5rem"
                h="2.5rem"
                color="content.brand"
                zIndex="1"
              />
            </Flex>

            <Flex flexDir="column" minH="6.25rem" gap="xs">
              <Text size="l">
                {publishedAt ? getPTBRDateAndRemoveYear(publishedAt) : ''} •{' '}
                {programTitle}
              </Text>

              <Text size="2xl" fontWeight="semibold" noOfLines={2}>
                {title}
              </Text>
            </Flex>
          </Flex>

          <Flex
            position="absolute"
            left="0"
            w="16.875rem"
            h="19.625rem"
            bg={`linear-gradient(180deg, rgba(3, 2, 10, 0.20) 21%, #000 100%)`}
            zIndex="10"
          />

          <Image
            src={thumb ?? ''}
            width={270}
            height={314}
            alt={programTitle}
            style={{
              borderRadius: '0.375rem',
              objectFit: 'cover',
            }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/eqi-placeholder.png';
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

type TopContentCardProps = Pick<TopContent, 'thumb' | 'title'> & {
  path: TopContent['slug'];
  position: number;
  programTitle: TopContent['program_title'];
  publishedAt: TopContent['published_at'];
};
